import React from 'react';
import { Helmet } from 'react-helmet';
import { JsonLdScript } from 'components/atoms';
import { useSelector } from 'react-redux';
import { HelmetConfig } from 'config/helmet';
import featuredCities from 'config/featuredCities';
import storageTypes from 'config/storageTypes';
import unitsSizes from 'config/unitsSizes';
import useSearchHandler from 'hooks/useSearchHandler';
import usePageViewTracking from 'hooks/usePageViewTracking';
import homepageSelector from 'store/selectors/homepage';

import swerve from 'assets/images/layout/swerve-background-bottom-2.svg';

import { Page } from 'components/core/Page';
import { Image } from 'components/core/Image';
import {
	HomeHeroBanner,
	WhyPeopleChoose,
	FeaturedCities,
	StorageTypes,
	ArgusHeroBanner,
	StorageUnitSize,
} from 'components/landing';
import { StickySearch } from 'components/search/StickySearch';
import { TrustPilotReviewSection } from 'components/landing/TrustPilotReviewSectionV2';

import './HomePage.scss';
import { PAGE_TYPES } from '@sparefoot/tracking-events';

export function HomePage() {
	const { trackingPixel, meta } = useSelector(homepageSelector);

	const handleSearch = useSearchHandler({});
	usePageViewTracking(PAGE_TYPES.HOME_PAGE);

	const orgSchema = {
		'@context': 'http://schema.org',
		'@type': 'Organization',
		name: 'SelfStorage.com',
		telephone: '(877) 688 4856',
		url: 'https://www.selfstorage.com/',
		sameAs: [
			'https://www.linkedin.com/company/selfstorage.com',
			'https://twitter.com/selfstorageinc',
			'https://www.facebook.com/SelfStorageInc',
			'https://www.youtube.com/user/SelfStorageCom',
			'https://www.crunchbase.com/organization/selfstorage-com#/entity',
		],
	};

	const searchSchema = {
		'@context': 'http://schema.org',
		'@type': 'WebSite',
		name: 'SelfStorage.com',
		url: 'https://www.selfstorage.com/',
		potentialAction: {
			'@type': 'SearchAction',
			target: 'https://www.selfstorage.com/search?location={search_term_string}',
			'query-input': 'required name=search_term_string',
		},
	};

	return (
		<Page
			className="home-page"
			pageType={PAGE_TYPES.HOME}
			trackingPixel={trackingPixel}
			title={meta.title}
			description={meta.description}
			canonical={meta.canonical}
		>
			<Helmet>
				<meta
					name="google-site-verification"
					content={HelmetConfig.googleSiteVerification}
				/>
				<meta
					name="msvalidate.01"
					content={HelmetConfig.msValidate1}
				/>
			</Helmet>
			<JsonLdScript schema={orgSchema} />
			<JsonLdScript schema={searchSchema} />
			<HomeHeroBanner />

			<StickySearch
				hidden
				horizontal
				onSearch={handleSearch}
			/>
			<ArgusHeroBanner />
			<WhyPeopleChoose />
			<StorageUnitSize units={unitsSizes} />
			<StorageTypes types={storageTypes} />
			<TrustPilotReviewSection />
			<Image
				className="reviews-divider"
				src={swerve}
			/>
			<FeaturedCities cities={featuredCities} />
		</Page>
	);
}

export default HomePage;
